import * as React from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { actions } from 'Store/myapp/actions';
import DocumentHeader from 'Components/DocumentHeader';
import DocumentWrapper from 'Components/DocumentWrapper';
import { FormContextProvider } from 'Containers/FormContextHandler';
import FormEditingHandler from 'Containers/FormEditingHandler';
import PatientReportedSeizureForm from './Form/PatientReportedSeizureForm';
import SeizuresHistory from './HistoryRowData';
import StandAloneFormEditingHandler from 'Containers/StandAloneFormEditingHandler';
import ActionButtonRounded from 'Components/ActionButtonRounded';
import type { TRelevantDocumentType } from '../utils';

const Seizure = ({ documents }: IOwnProps): React.JSX.Element => {
  const [patientReportedSeizureFormOpen, setPatientReportedSeizureFormOpen] = React.useState<
    { doc: IDocumentWithTypeV2<'patientReportedSeizure'>; type: 'edit' | 'view' } | undefined
  >(undefined);
  const [tempPatientReportedSeizure, setTempPatientReportedSeizure] = React.useState<
    IDocumentWithTypeV2<'patientReportedSeizure'> | undefined
  >(undefined);
  const [openTab, setOpenTab] = React.useState<number>(0);
  const [saving, setSaving] = React.useState<boolean>(false);
  const { formatMessage } = useIntl();
  const fm = (id: string) => formatMessage({ id });
  const dispatch = useDispatch();

  const savePatRepSeiz = async () => {
    setSaving(true);
    await actions.putNewCommit(
      'patientReportedSeizure',
      patientReportedSeizureFormOpen?.doc._id ?? '',
      tempPatientReportedSeizure,
      dispatch,
    );
    setSaving(false);
    setPatientReportedSeizureFormOpen(undefined);
  };

  const handleFormChange = (values: TOnChangeValues): void => {
    const name = Object.keys(values)[0];
    if (!name) return;
    const value = values[name];
    if (name) {
      setTempPatientReportedSeizure(prev => ({
        ...(prev as IDocumentWithTypeV2<'patientReportedSeizure'>),
        [name]: value,
      }));
    }
  };

  React.useEffect(() => {
    if (patientReportedSeizureFormOpen?.doc) {
      setTempPatientReportedSeizure(structuredClone(patientReportedSeizureFormOpen.doc));
    }
  }, [patientReportedSeizureFormOpen]);

  const seizureDocuments = documents.filter(d => d._type !== 'seizureType');

  return (
    <>
      {patientReportedSeizureFormOpen ? (
        <>
          <StandAloneFormEditingHandler
            currentPageName={'seizure'}
            headerLocaleId={'seizure.title'}
            viewing={patientReportedSeizureFormOpen.type === 'view'}
            endViewingAction={() => setPatientReportedSeizureFormOpen(undefined)}
            cancelEditingAction={() => {
              setPatientReportedSeizureFormOpen(undefined);
              setTempPatientReportedSeizure(undefined);
            }}
            saveAction={() => savePatRepSeiz()}
            loading={saving}
          >
            <PatientReportedSeizureForm
              fm={fm}
              formData={{ document: tempPatientReportedSeizure ?? {}, onChange: handleFormChange }}
              editing={patientReportedSeizureFormOpen.type === 'edit'}
            />
          </StandAloneFormEditingHandler>
        </>
      ) : (
        <FormEditingHandler name="seizure" documents={seizureDocuments}>
          {(editing, startEdit, formData, view, saveDraft): React.JSX.Element => {
            return (
              <DocumentWrapper>
                <DocumentHeader
                  headerId="seizure.title"
                  editButtons={
                    !(editing || view?.viewing)
                      ? [
                          <ActionButtonRounded
                            key="newSeizure"
                            text={'seizure.addNew'}
                            onClick={startEdit({}, 'seizure', { status: 'accepted' })}
                          />,
                        ]
                      : undefined
                  }
                />

                <FormContextProvider
                  context={{
                    documents: seizureDocuments,
                    editing,
                    formData,
                    view,
                    startEdit,
                    saveDraft,
                  }}
                >
                  <SeizuresHistory
                    props={{ documents, editing, formData, view, startEdit, saveDraft, fm }}
                    setPatientReportedSeizureFormOpen={setPatientReportedSeizureFormOpen}
                    openTab={openTab}
                    setOpenTab={setOpenTab}
                  />
                </FormContextProvider>
              </DocumentWrapper>
            );
          }}
        </FormEditingHandler>
      )}
    </>
  );
};

interface IOwnProps {
  documents: Array<IDocumentWithTypeV2<TRelevantDocumentType>>;
}

export default Seizure;
